import { useEffect, useMemo, useState } from 'react';
import { xor } from 'lodash';
import equal from 'fast-deep-equal/es6/react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Dispatch } from 'redux';
import { push } from 'redux-first-history';
import { SelectedDates } from '@rossum/components/date-time-range-picker/types';
import { Box, Button, Paper, Stack } from '@rossum/ui/material';
import { useUnpaginatedUsers } from '../../business/users/useUnpaginatedUsers';
import InfoBox from '../../components/UI/InfoBox/index';
import { apiUrl } from '../../constants/config';
import { CONTACT_FORM_LINK } from '../../constants/values';
import { filterWorkspaces } from '../../features/queues/helpers/filterWorkspaces';
import { useWorkspacesWithQueues } from '../../features/queues/hooks/useWorkspacesWithQueues';
import { formatDatepickerValue } from '../../lib/dateUtils';
import { asArray, asScalar, parse, stringify } from '../../lib/url';
import {
  canUseUsageReportsSelector,
  isTrialSelector,
  statisticsUserReportsDisabled,
} from '../../redux/modules/organization/selectors';
import {
  formatDateQuery,
  parseDateQuery,
} from '../../redux/modules/statistics/helpers';
import { enterStatistics } from '../../redux/modules/ui/actions';
import { filterUsers, usersToOptions } from '../../redux/modules/users/helpers';
import { State } from '../../types/state';
import { UserList } from '../../types/user';
import DisabledOverlay, {
  getDisabledOverlayBgColor,
  LIST_SIDEBAR_WIDTH,
} from './components/DisabledOverlay';
import EmptySeries from './components/EmptySeries';
import HidingButtons from './components/HidingButtons';
import { OptionT } from './components/Picker';
import { StatisticsDatePicker } from './components/StatisticsDatePicker';
import {
  AutomatedAggregate,
  CorrectionsAggregate,
  DelayAggregate,
  RejectionAggregate,
  TimeAggregate,
  TimePerDocumentAggregate,
  UsageAggregate,
} from './containers/Aggregates';
import Granularity, {
  granularities,
  GranularityT,
} from './containers/Granularity';
import AutomatedGraph from './containers/Graphs/AutomatedGraph';
import CorrectionsGraph from './containers/Graphs/CorrectionsGraph';
import DelayGraph from './containers/Graphs/DelayGraph';
import RejectionGraph from './containers/Graphs/RejectionGraph';
import TimeAndCorrectionsPerFieldTable from './containers/Graphs/TimeAndCorrectionsPerFieldTable';
import TimeGraph from './containers/Graphs/TimeGraph';
import TimePerDocumentGraph from './containers/Graphs/TimePerDocument';
import UsageGraph from './containers/Graphs/UsageGraph';
import QueuePicker from './containers/QueuePicker';
import UserPicker from './containers/UserPicker';
import { useUsageReportExport } from './hooks/useUsageReportExport';
import styles from './styles.module.sass';

const GRAPH_CONTAINER_WIDTH = 1100;

type StateProps = {
  beginDate: string | null;
  canUseUsageReports: boolean | undefined;
  defaultQueues: Array<number>;
  defaultUsers: Array<number>;
  endDate: string | null;
  groupBy: GranularityT;
  isTrial: boolean;
};

type OwnProps = RouteComponentProps;

type DispatchProps = {
  setSelectionToQuery: (query: Record<string, unknown>) => void;
};

type Props = OwnProps & DispatchProps & StateProps;

const getIdsFromResult = <T extends { id: number }>(result: T[]) =>
  result.map(({ id }) => id);

const workspacesToOptions = <
  T extends { name: string; queues: { name: string; id: number }[] },
>(
  workspaces: T[]
): OptionT[] =>
  workspaces
    .filter(({ queues }) => queues.length)
    .map(({ name, queues }) => ({
      label: name,
      value: queues.map(({ name: _name, id }) => ({
        label: _name,
        value: id,
      })),
    }));

const Statistics = ({
  beginDate,
  defaultQueues,
  defaultUsers: unresolvedDefaultUsers,
  endDate,
  groupBy,
  location,
  setSelectionToQuery,
  isTrial,
  canUseUsageReports,
}: Props) => {
  const intl = useIntl();

  const [activeGranularity, setActiveGranularity] =
    useState<GranularityT>(groupBy);

  const { normalizedBeginDate, normalizedEndDate } = useMemo(() => {
    const normalizedBeginDate = beginDate ? parseDateQuery(beginDate) : null;
    const normalizedEndDate = endDate ? parseDateQuery(endDate) : null;
    return { normalizedEndDate, normalizedBeginDate };
  }, [beginDate, endDate]);

  const [selectedDates, setSelectedDates] = useState<SelectedDates>([
    normalizedBeginDate,
    normalizedEndDate,
  ]);

  const { workspacesWithQueues: workspaces, queues: queuesData } =
    useWorkspacesWithQueues({
      enableQueries: true,
    });

  const queuesIdList = useMemo(
    () => (queuesData ? getIdsFromResult(queuesData) : undefined),
    [queuesData]
  );

  const { data: deletedUsersIdList } = useUnpaginatedUsers(
    { deleted: true },
    { select: getIdsFromResult }
  );

  const { data: usersList, isFetched: usersLoaded } = useUnpaginatedUsers({
    deleted: false,
  });

  const allUsers = usersList as UserList | undefined;

  const usersIdList = useMemo(
    () => (usersList ? getIdsFromResult(usersList) : undefined),
    [usersList]
  );

  const dispatch = useDispatch();

  // instead of onEnter/onExit on parent route
  // compatibility issue
  useEffect(() => {
    if (queuesIdList && usersIdList && deletedUsersIdList) {
      dispatch(
        enterStatistics({
          queuesIdList,
          usersIdList,
          deletedUsersIdList,
        })
      );
    }
  }, [deletedUsersIdList, dispatch, queuesIdList, usersIdList]);

  const userReportsDisabled = useSelector(statisticsUserReportsDisabled);
  const defaultUsers = useMemo(
    () => (userReportsDisabled ? usersIdList : unresolvedDefaultUsers),
    [unresolvedDefaultUsers, userReportsDisabled, usersIdList]
  );
  const [users, setUsers] = useState(defaultUsers);
  const [queues, setQueues] = useState(defaultQueues);
  const [queuesSearch, setQueuesSearch] = useState('');
  const [usersSearch, setUsersSearch] = useState('');
  const [queuePickerKey, setQueuePickerKey] = useState(Date.now());

  const { isExporting, triggerExport } = useUsageReportExport();

  const statistics = useSelector((state: State) => state.statistics);

  const hasSomeStatisticsData = statistics.series.length > 0;
  const isTemporary = statistics.isTmpState;
  const { series, totals } = statistics;

  const queuesOptions = useMemo<OptionT[]>(
    () =>
      workspacesToOptions(
        queuesSearch
          ? filterWorkspaces(queuesSearch, workspaces ?? [])
          : (workspaces ?? [])
      ),
    [queuesSearch, workspaces]
  );

  const usersOptions = useMemo<OptionT[]>(
    () =>
      usersToOptions(
        usersSearch && allUsers
          ? filterUsers(usersSearch, allUsers)
          : (allUsers ?? [])
      ),
    [allUsers, usersSearch]
  );

  useEffect(() => setActiveGranularity(groupBy), [groupBy]);

  useEffect(() => {
    if (normalizedBeginDate && normalizedEndDate)
      setSelectedDates([normalizedBeginDate, normalizedEndDate]);
  }, [normalizedBeginDate, normalizedEndDate]);

  useEffect(() => {
    setQueues(defaultQueues);
    if (!equal(defaultQueues, queues)) {
      setQueuePickerKey(Date.now());
    }
  }, [defaultQueues]); // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUsers(defaultUsers);
  }, [defaultUsers]);

  const selectionNotChanged =
    !xor(defaultQueues, queues).length &&
    !xor(defaultUsers, users).length &&
    groupBy === activeGranularity;

  const showBanner = isTrial || canUseUsageReports === undefined;
  const statisticsDisabled = canUseUsageReports === false;
  const isTmpState = statisticsDisabled || isTemporary;

  const downloadGroupBy = [groupBy, 'workspace', 'queue'];
  const downloadGroupByWithUsers = [...downloadGroupBy, 'user'];

  const hasBothDates = Boolean(selectedDates[0] && selectedDates[1]);
  return (
    <Stack
      direction="column"
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {statisticsDisabled && <DisabledOverlay link={CONTACT_FORM_LINK} />}
      <Stack
        component={Paper}
        sx={{
          top: 0,
          bottom: 0,
          borderRadius: 0,
          overflow: 'hidden',
          position: 'absolute',
          flexDirection: 'column',
          width: 300,
        }}
      >
        <QueuePicker
          titleId="containers.statistics.sidebar.title.queues"
          onSearch={setQueuesSearch}
          options={queuesOptions}
          isSearched={!!queuesSearch}
          picked={queues}
          onPick={setQueues}
          key={queuePickerKey}
          queuesLoaded={workspaces !== undefined}
        />
        {!userReportsDisabled && (
          <UserPicker
            isSearched={false}
            onSearch={setUsersSearch}
            options={usersOptions}
            picked={users ?? []}
            onPick={setUsers}
            usersLoaded={usersLoaded}
            deletedUsersIdList={deletedUsersIdList ?? []}
          />
        )}
        {statisticsDisabled && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              width: LIST_SIDEBAR_WIDTH,
              top: 0,
              bottom: 0,
              backgroundColor: getDisabledOverlayBgColor,
            }}
          />
        )}
      </Stack>
      <Stack
        direction="column"
        sx={{
          height: '100%',
          overflow: 'hidden',
          marginLeft: '300px',
        }}
      >
        <Box sx={{ width: '100%', height: '100%', overflowY: 'auto' }}>
          {showBanner && (
            <Stack
              className={styles['StatisticsInfoBox']}
              sx={{ pt: 2, marginLeft: '20px' }}
            >
              <InfoBox
                title="containers.statistics.upgrade.title"
                text="containers.statistics.upgrade.text"
              />
            </Stack>
          )}
          <Stack className={styles['Header']} direction="row">
            <Stack spacing={2} direction="row">
              {activeGranularity && (
                <>
                  <StatisticsDatePicker
                    dateLabel={
                      normalizedBeginDate && normalizedEndDate
                        ? formatDatepickerValue(
                            normalizedBeginDate,
                            normalizedEndDate,
                            intl.locale
                          )
                        : ''
                    }
                    selectedDates={selectedDates}
                    setSelectedDates={setSelectedDates}
                    onCancel={() =>
                      setSelectedDates([normalizedBeginDate, normalizedEndDate])
                    }
                    onConfirm={params =>
                      setSelectionToQuery({
                        endDate: params.endDate,
                        beginDate: params.beginDate,
                      })
                    }
                  />
                  <Granularity
                    setActiveGranularity={(groupByValue: GranularityT) =>
                      setActiveGranularity(groupByValue)
                    }
                    activeGranularity={activeGranularity}
                  />
                </>
              )}
            </Stack>
            <Button
              variant="outlined"
              color="secondary"
              disabled={isExporting || !hasBothDates}
              onClick={() =>
                selectedDates[0] &&
                selectedDates[1] &&
                triggerExport({
                  filter: {
                    users: (users ?? []).map(id => `${apiUrl}/users/${id}`),
                    queues: queues.map(id => `${apiUrl}/queues/${id}`),
                    beginDate: formatDateQuery(selectedDates[0]),
                    endDate: formatDateQuery(selectedDates[1]),
                  },
                  groupBy: userReportsDisabled
                    ? downloadGroupBy
                    : downloadGroupByWithUsers,
                })
              }
              data-cy="statistics-download-usage-report-button"
            >
              {intl.formatMessage({
                id: 'components.statistics.downloadUsageReport',
              })}
            </Button>
          </Stack>
          <Stack maxWidth={GRAPH_CONTAINER_WIDTH}>
            {hasSomeStatisticsData ? (
              <Stack direction="row" flexWrap="wrap" p={2.5} gap={2}>
                <UsageGraph
                  series={series}
                  isTmpState={isTmpState}
                  groupBy={groupBy}
                >
                  <UsageAggregate totals={totals} isTmpState={isTmpState} />
                </UsageGraph>
                <TimeGraph
                  series={series}
                  isTmpState={isTmpState}
                  total={totals.turnaroundAvgS ?? 0}
                  groupBy={groupBy}
                >
                  <TimeAggregate totals={totals} isTmpState={isTmpState} />
                </TimeGraph>
                <CorrectionsGraph
                  series={series}
                  isTmpState={isTmpState}
                  groupBy={groupBy}
                >
                  <CorrectionsAggregate
                    totals={totals}
                    isTmpState={isTmpState}
                  />
                </CorrectionsGraph>
                <DelayGraph
                  series={series}
                  isTmpState={isTmpState}
                  groupBy={groupBy}
                >
                  <DelayAggregate totals={totals} isTmpState={isTmpState} />
                </DelayGraph>
                <TimePerDocumentGraph
                  series={series}
                  isTmpState={isTmpState}
                  total={totals.timePerDocumentAvgS ?? 0}
                  groupBy={groupBy}
                >
                  <TimePerDocumentAggregate
                    totals={totals}
                    isTmpState={isTmpState}
                  />
                </TimePerDocumentGraph>
                <TimeAndCorrectionsPerFieldTable
                  series={series}
                  groupBy={groupBy}
                  key={location.search}
                />
                <AutomatedGraph
                  series={series}
                  isTmpState={isTmpState}
                  groupBy={groupBy}
                >
                  <AutomatedAggregate totals={totals} isTmpState={isTmpState} />
                </AutomatedGraph>
                <RejectionGraph
                  series={series}
                  isTmpState={isTmpState}
                  groupBy={groupBy}
                >
                  <RejectionAggregate totals={totals} isTmpState={isTmpState} />
                </RejectionGraph>
              </Stack>
            ) : (
              <EmptySeries inGraph={false} />
            )}
          </Stack>
        </Box>
        {beginDate && endDate && groupBy && (
          <HidingButtons
            hideCondition={(selectionNotChanged && !!groupBy) || !usersLoaded}
            disabled={!users?.length || !queues.length}
            onClickCancel={() => {
              setUsers(defaultUsers);
              setQueues(defaultQueues);
              setActiveGranularity(groupBy);
            }}
            onClickApply={() =>
              setSelectionToQuery({
                queues,
                users,
                groupBy: activeGranularity,
              })
            }
          />
        )}
      </Stack>
    </Stack>
  );
};

const assureArrayOfNumbers = (
  parsedItem: string | Array<string> | null
): Array<number> => asArray(parsedItem).map(Number);

export const mapStateToProps = (state: State): StateProps => {
  const {
    queues = [],
    users = [],
    groupBy,
    beginDate,
    endDate,
  } = parse(state.router.location.search);

  return {
    beginDate: asScalar(beginDate) ?? null,
    canUseUsageReports: canUseUsageReportsSelector(state),
    defaultQueues: assureArrayOfNumbers(queues),
    defaultUsers: assureArrayOfNumbers(users),
    endDate: asScalar(endDate) ?? null,
    groupBy:
      granularities.find(granularity => granularity === asScalar(groupBy)) ??
      'week',
    isTrial: !!isTrialSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  { location: { search } }: OwnProps
) => ({
  setSelectionToQuery: (query: Record<string, unknown>) =>
    dispatch(push({ search: stringify({ ...parse(search), ...query }) })),
});

export default connect<StateProps, DispatchProps, OwnProps, State>(
  mapStateToProps,
  mapDispatchToProps
)(Statistics);
